import Vue from 'vue';
import driftScript, { driftLoad } from 'client/GrowthEngineering/utilities/driftScript';
import driftScriptConfig from 'client/GrowthEngineering/utilities/driftScriptConfig';

const initialState = () => ({
  chatMessage: {},
  conversationStarted: false,
  hasLoadingError: false,
  hasNewMessage: false,
  interactionId: null,
  isLoading: false,
  isOpen: false,
  orgId: null,
  user: {},
});

const eventListeners = {};

const toggleClass = (className, operation, token) => {
  const element = document.getElementsByClassName(className)[0];
  if (element && element.classList) {
    element.classList[operation](`jc-${token}`);
  }
};

const addStyleClasses = (token, classNames = ['drift-frame-chat']) => {
  classNames.forEach((className) => {
    toggleClass(className, 'add', token);
  });
};
const removeStyleClasses = (token, classNames = ['drift-frame-chat']) => {
  classNames.forEach((className) => {
    toggleClass(className, 'remove', token);
  });
};

const createEventListener = (fn, token, classNames) => () => fn(token, classNames);

// event listeners are for the control frame only
const removeEventListeners = (chatName) => {
  const { drift } = window;

  if (eventListeners[chatName]) {
    // remove existing listeners when new widget is initialized
    drift.off('chatClose', eventListeners[chatName].chatClose);
    drift.off('chatOpen', eventListeners[chatName].chatOpen);

    delete eventListeners[chatName];
  }
};

const addEventListeners = (chatName) => {
  const { drift } = window;

  let className = 'drift-frame-chat';
  if (chatName === 'paymentChat') {
    className = 'drift-frame-controller';
  }

  eventListeners[chatName] = {
    chatClose: createEventListener(removeStyleClasses, chatName, [className]),
    chatOpen: createEventListener(addStyleClasses, chatName, [className]),
  };

  drift.on('chatClose', eventListeners[chatName].chatClose);
  drift.on('chatOpen', eventListeners[chatName].chatOpen);
};

const getAttributes = (state) => {
  const { orgId, user } = state;
  const { email, firstname, lastname } = user;
  return {
    email,
    orgId,
    firstName: firstname,
    lastName: lastname,
    name: `${firstname} ${lastname}`,
  };
};

const activateChat = (commit, state, api) => {
  const {
    interactionId,
    isOpen,
    conversationStarted,
    hasNewMessage,
  } = state;
  const { drift } = window;

  const attributes = getAttributes(state);

  if (!isOpen && !conversationStarted && !hasNewMessage) {
    drift.setUserAttributes(attributes);
    api.startInteraction({ interactionId });

    commit('CONVERSATION_STARTED', true);
  }

  if (api) {
    api.sidebar.toggle();
  } else {
    drift.on('ready', (apiData) => {
      apiData.sidebar.toggle();
    });
  }

  commit('UPDATE_CHAT_NEW_MESSAGE', {});
  commit('IS_OPEN', !state.isOpen);
};

export const createChatModule = () => ({
  namespaced: true,
  state: initialState(),
  actions: {
    loadScript({ commit, state }) {
      if (!window.drift) {
        const attributes = getAttributes(state);
        driftScript(attributes);
        driftScriptConfig(
          payload => commit('IS_OPEN', payload),
          payload => commit('UPDATE_CHAT_NEW_MESSAGE', payload),
        );
      }
    },
    toggleChat({ commit, state }, { interactionId, chatName }) {
      const { drift } = window;
      const { isOpen, conversationStarted, hasNewMessage } = state;
      const attributes = getAttributes(state);

      const updateClassesAndListeners = () => {
        if (state.interactionId !== interactionId) {
          // if the interactionId has changed, then this is a different
          // "instance" of the chat. We need to update styles and
          // event listeners.
          removeStyleClasses(state.chatName);
          removeEventListeners(state.chatName); // remove existing event listeners

          // save data in state
          commit('UPDATE_INTERACTION_ID', interactionId);
          commit('UPDATE_CHAT_NAME', chatName);
          commit('CONVERSATION_STARTED', false); // reset the state of the widget

          // add styles and event listeners to this new "instance"
          addStyleClasses(chatName);
          addEventListeners(chatName);
        }
      };

      // api is actually the data returned from Drift
      // their docs use api as the variable name, so we went with it
      if (!drift || !drift.api) {
        commit('HAS_LOADING_ERROR', true);
      } else if (!isOpen && !conversationStarted && !hasNewMessage) {
        // Show the loading icon while chat loads
        commit('IS_LOADING', true);

        // If ten seconds passes there was probably an error
        // reset loading to make the chat icon clickable, try again
        setTimeout(() => {
          commit('IS_LOADING', false);
        }, 10000);

        if (!drift.ready) driftLoad(attributes);

        drift.on('ready', (api) => {
          commit('IS_LOADING', false);
          api.widget.hide();
          updateClassesAndListeners();
          activateChat(commit, state, api);
        });

        // After an update to the widget and script from drift (Jan 2021)
        // drift is not always ready when the chat button has been clicked
        // If drift is not ready at this point call drift.load again
      } else {
        updateClassesAndListeners();
        activateChat(commit, state, drift.api);
      }
    },
    updateOrg({ commit }, payload) {
      commit('UPDATE_ORG', payload);
    },
    updateUserSettings({ commit }, payload) {
      commit('UPDATE_USER_SETTINGS', payload);
    },
  },
  getters: {
    getChatMessage(state) {
      return state.chatMessage;
    },
    getConversationStarted(state) {
      return state.conversationStarted;
    },
    getIsLoading(state) {
      return state.isLoading;
    },
    getIsOpen(state) {
      return state.isOpen;
    },
    getHasLoadingError(state) {
      return state.hasLoadingError;
    },
  },
  mutations: {
    CONVERSATION_STARTED: (state, payload) => {
      Vue.set(state, 'conversationStarted', payload);
    },
    HAS_LOADING_ERROR: (state, payload) => {
      Vue.set(state, 'hasLoadingError', payload);
    },
    IS_LOADING: (state, payload) => {
      Vue.set(state, 'isLoading', payload);
    },
    IS_OPEN: (state, payload) => {
      Vue.set(state, 'isOpen', payload);
    },
    UPDATE_CHAT_NEW_MESSAGE: (state, payload) => {
      Vue.set(state, 'chatMessage', payload);
    },
    UPDATE_INTERACTION_ID: (state, payload) => {
      Vue.set(state, 'interactionId', payload);
    },
    UPDATE_ORG: (state, payload) => {
      Vue.set(state, 'orgId', payload);
    },
    UPDATE_USER_SETTINGS: (state, payload) => {
      Object.keys(payload).forEach((key) => {
        Vue.set(state.user, key, payload[key]);
      });
    },
    UPDATE_CHAT_NAME: (state, payload) => {
      Vue.set(state, 'chatName', payload);
    },
  },
});

export default createChatModule();
